// Pago de la factura del plan con Débito Inmediato (BNC), desde la app del
// cliente.
//
// Tres pasos: elige la cuenta (una guardada o una nueva), el banco le manda una
// clave a su teléfono, la escribe y con eso se debita y se registra el pago. La
// referencia la pone el banco.
//
// Dos reglas que gobiernan todo lo demás:
//   1. El monto NO se calcula acá. Lo cotiza el servidor (`getDebitQuote$`) y es
//      el mismo con el que cobra: el banco ata la clave al monto exacto en
//      bolívares, así que un número distinto en pantalla sería una autorización
//      por una cifra que no es la que sale de la cuenta.
//   2. Si el resultado es indeterminado o el débito entró pero el pago no quedó
//      registrado, NO se ofrece pagar de nuevo: reintentar el cobro debitaría dos
//      veces. Ese caso lo acredita soporte con la referencia, así que acá no hay
//      ningún botón: solo el aviso de que no vuelva a pagar.
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VE_MOBILE_PREFIXES_TEXT } from '@common/constants/ve-mobile-prefixes';
import { DebitoInmediatoAccountInput, DebitoInmediatoBank, DebitoInmediatoChangeOptions, DebitoInmediatoQuote, DebitoInmediatoSavedAccount } from '@common/models';
import { AnalyticsService, PlansService, ToastService } from '@common/services';
import { EXTRA_USERS_SALES_DISABLED_MESSAGE, EXTRA_USERS_SALES_DISABLED_TITLE, isExtraUsersSalesDisabledError } from '@common/utils/extra-users.util';
import { FISCAL_QUOTA_REJECTED, fiscalQuotaIssue, FiscalQuotaIssueCode } from '@common/utils/fiscal-quota.util';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';

import { nationalIdValidator, venezuelanMobileValidator } from './debito-inmediato-account.validators';

/** Desenlace del débito, ya interpretado desde la respuesta del servidor. */
export interface DebitoInmediatoOutcome {
    /**
     * - `charged`: el banco debitó y el pago quedó registrado.
     * - `rejected`: el banco no debitó (fondos, clave, datos). Se puede reintentar.
     * - `indeterminate`: no se sabe si debitó, o debitó y el registro falló. Nunca
     *   se reintenta el cobro; se resuelve por referencia.
     * - `processing`: el pago QUEDÓ REGISTRADO y espera validación. Pasa cuando el
     *   banco del cliente no confirmó la operación dentro de su ventana, o la
     *   rechazó después de haberla aceptado. No se reintenta desde acá: ya hay un
     *   pago registrado y lo resuelve FINA.
     */
    status: 'charged' | 'rejected' | 'indeterminate' | 'processing';
    /** El dinero YA salió de la cuenta del cliente. */
    charged: boolean;
    reference: string | null;
    /** El rechazo quemó la clave: hace falta pedir otra. */
    requiresNewToken: boolean;
    title: string;
    message: string;
    bankCode: string | null;
    bankDetail: string | null;
}

/**
 * Detalle técnico del banco en una línea: `"AM04 — Fondos insuficientes"`. Solo
 * el código, solo el texto, o nada.
 */
export const formatBankDetail = (code: string | null | undefined, detail: string | null | undefined): string | null => {
    const cleanCode = String(code ?? '').trim();
    const cleanDetail = String(detail ?? '').trim();

    if (cleanCode && cleanDetail) return `${cleanCode} — ${cleanDetail}`;
    return cleanCode || cleanDetail || null;
};

/**
 * Evento con el que se sigue cada desenlace en PostHog. Es el único lugar donde
 * se ve, sin entrar a los logs, cuántos pagos terminan sin confirmar y cuántos
 * quedan en validación esperando al banco del cliente.
 */
const OUTCOME_EVENTS: Record<DebitoInmediatoOutcome['status'], string> = {
    charged: 'Débito inmediato pagado',
    rejected: 'Débito inmediato rechazado',
    indeterminate: 'Débito inmediato sin confirmar',
    processing: 'Débito inmediato en validación',
};

/** Respaldo de `quoteError` cuando el servidor no manda `message`. */
const QUOTE_ERROR_FALLBACK = 'No pudimos calcular el monto a pagar. Intenta de nuevo en unos minutos.';

@Component({
    selector: 'fina-debito-inmediato-payment',
    templateUrl: './debito-inmediato-payment.component.html',
    styleUrls: ['./debito-inmediato-payment.component.scss'],
})
export class DebitoInmediatoPaymentComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Vida de la clave del banco. BNC la vence 2 minutos después de que se la
     * PIDEN, no de que llega al teléfono: el SMS puede tardar, y ese retardo se
     * come parte de la ventana. Por eso el contador arranca cuando sale nuestra
     * petición y no cuando responde el banco.
     */
    static readonly OTP_TTL_SECONDS = 120;

    /**
     * Cada cuánto se le pregunta al banco si ya liquidó el débito. Es el
     * intervalo que indicó BNC para consultar sus operaciones.
     */
    static readonly SETTLEMENT_POLL_MS = 3000;

    /**
     * Cuánto puede tardar el banco del cliente en liquidar un débito
     * interbancario. Pasado ese tiempo BNC anula la operación, así que seguir
     * preguntando no aporta: el pago (ya registrado) pasa a validación.
     */
    static readonly SETTLEMENT_WINDOW_SECONDS = 120;

    /** Factura a pagar. */
    @Input() billPlanId: string | null = null;
    /** Cambio de plan que el cliente esté eligiendo (cambia el monto cotizado). */
    @Input() planType: string | null = null;
    /** Cambio de frecuencia (cambia el monto cotizado). */
    @Input() paymentFrequency: string | null = null;
    /**
     * Cupos pedidos en el checkout (cambian el monto cotizado). `null` sin
     * intención de cupos: el servidor cotiza entonces con los facturados.
     */
    @Input() pendingPurchasedUsers: number | null = null;
    /** Usuarios que se bloquean al confirmar; viajan solo junto a `pendingPurchasedUsers`. */
    @Input() pendingBlockUserIds: string[] | null = null;
    /**
     * Cupo fiscal extra pedido (ADM-740; cambia el monto cotizado). `null` sin intención:
     * la clave no viaja y el servidor cotiza con lo facturado.
     */
    @Input() extraControlNumbers: number | null = null;

    /** El pago quedó registrado: el contenedor puede pasar al paso de éxito. */
    @Output() paid = new EventEmitter<void>();
    /**
     * El cobro no cerró limpio (indeterminado, o debitado sin registrar). No es
     * éxito ni rechazo: el contenedor no debe mostrar éxito ni ofrecer otro cobro.
     */
    @Output() chargeUnresolved = new EventEmitter<DebitoInmediatoOutcome>();
    /**
     * `true` desde que el débito sale hacia el banco hasta que hay un desenlace
     * (incluida la espera de la liquidación, que puede durar 2 minutos). En ese
     * lapso puede haber dinero moviéndose: el contenedor debe impedir cambiar a
     * otra vía de pago, porque destruir este componente corta el seguimiento y
     * deja la puerta abierta a reportar un segundo pago de la misma factura.
     */
    @Output() chargeInFlight = new EventEmitter<boolean>();
    /**
     * Error del cupo fiscal en `quote`, `request-token` o `charge` (ADM-740): uno de los
     * cuatro 409 o el 400 `FISCAL_QUOTA_REJECTED`. No hubo débito: no es un rechazo del
     * banco. Los efectos (refrescar el bundle, volver al paso 1) los hace el contenedor.
     */
    @Output() fiscalQuotaConflict = new EventEmitter<FiscalQuotaIssueCode>();

    /**
     * `settling`: el pago ya está registrado y se está esperando que el banco del
     * cliente confirme el débito. No es un paso que el cliente tenga que
     * completar: puede cerrar la ventana y su pago sigue en pie.
     */
    step: 'account' | 'otp' | 'settling' | 'result' = 'account';

    /**
     * Pasa a true en `ngOnInit`. La elegibilidad la decide `form-pay-plan`
     * (`isDebitoInmediatoEligible`) antes de montarlo; esto solo evita que el
     * `ngOnChanges` de los inputs iniciales cotice antes de la carga.
     */
    eligible = false;
    loadingQuote = false;
    loadingAccounts = true;
    requestingToken = false;
    charging = false;

    quote: DebitoInmediatoQuote | null = null;
    quoteError = '';
    accounts: DebitoInmediatoSavedAccount[] = [];
    banks: DebitoInmediatoBank[] = [];
    /** Sin lista de bancos no se puede registrar una cuenta nueva. */
    banksUnavailable = false;

    /** `''` = usar una cuenta nueva; cualquier otro valor = cuenta guardada. */
    selectedAccountId = '';
    /** Teléfono al que el banco dijo haber mandado la clave. */
    tokenSentTo = '';
    /** Código + texto del banco cuando falla el envío de la clave. */
    tokenErrorDetail: string | null = null;
    outcome: DebitoInmediatoOutcome | null = null;

    /** Segundos de vida que le quedan a la clave. Lo refresca un tick de 1s. */
    otpSecondsLeft = 0;
    /** La clave venció sin usarse: hay que pedir otra, no se puede cobrar. */
    otpExpired = false;

    /** Segundos que le quedan al banco para confirmar el débito. */
    settlementSecondsLeft = 0;
    /** Referencia del débito que se está esperando confirmar. */
    settlementReference: string | null = null;

    readonly accountForm: FormGroup = this.formBuilder.group({
        phone: ['', [Validators.required, venezuelanMobileValidator]],
        cedula: ['', [Validators.required, nationalIdValidator]],
        bankCode: ['', [Validators.required]],
        holderName: ['', [Validators.required]],
    });

    readonly otpForm: FormGroup = this.formBuilder.group({
        otp: ['', [Validators.required, Validators.pattern(/^\d{4,8}$/)]],
    });

    private readonly destroy$ = new Subject<void>();
    /** Cotización en vuelo: solo la última manda (ver `loadQuote`). */
    private quoteRequest = 0;
    /** Instante en que se pidió la clave, contra el que se mide su vencimiento. */
    private otpRequestedAt: number | null = null;
    private otpTicker: Subscription | null = null;
    /** Fin de la ventana del banco (epoch ms) mientras se espera la liquidación. */
    private settlementDeadline: number | null = null;
    private settlementTicker: Subscription | null = null;
    /** Consulta en vuelo: no se apilan si el banco tarda más que el intervalo. */
    private settlementInFlight = false;
    /** El cliente ya eligió con qué cuenta paga: no se le cambia por debajo. */
    private accountChosenByUser = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly plansService: PlansService,
        private readonly toastService: ToastService,
        private readonly analyticsService: AnalyticsService,
    ) {}

    ngOnInit(): void {
        this.eligible = true;
        this.loadQuote();
        this.loadAccounts();
        this.loadBanks();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.eligible) {
            return;
        }
        // El monto depende de la factura, del cambio de plan/frecuencia, de los
        // cupos pedidos y del cupo fiscal: si el cliente cambia de opción en el paso
        // anterior hay que volver a cotizar, porque la clave del banco se pide por
        // un monto concreto.
        if (changes['billPlanId'] || changes['planType'] || changes['paymentFrequency'] || changes['pendingPurchasedUsers'] || changes['pendingBlockUserIds'] || changes['extraControlNumbers']) {
            // Con el pago ya registrado o un débito sin resolver no hay nada que
            // cotizar: el contenedor refresca la factura (pasa a `procesando` o
            // `pago`), los cupos dejan de viajar y una cotización nueva pisaría el
            // monto, o pintaría un error, encima del resultado.
            if (this.hasUnresolvedCharge || this.outcome?.status === 'charged') {
                return;
            }
            this.resetToAccountStepForNewAmount();
            this.loadQuote();
        }
    }

    /**
     * Cambió el monto a cotizar, así que la clave que el cliente ya tenga en
     * pantalla quedó atada a un monto que no es el que se va a debitar: se
     * vuelve al primer paso y se pide una nueva.
     *
     * Excepción: un débito sin resolver no se toca. Ahí el resultado es la única
     * salida segura (la referencia) y perderlo vale más que recotizar.
     */
    private resetToAccountStepForNewAmount(): void {
        if (this.step === 'account' || this.hasUnresolvedCharge) {
            return;
        }
        this.step = 'account';
        this.outcome = null;
        this.otpForm.reset({ otp: '' });
        this.stopOtpCountdown();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get usingSavedAccount(): boolean {
        return !!this.selectedAccountId;
    }

    /**
     * El cliente ya escribió algo de una cuenta nueva. Se mira el valor y no
     * `dirty` porque lo que importa es que haya datos cargados, sin depender de
     * cómo llegaron ahí.
     */
    private get newAccountFormHasData(): boolean {
        return Object.values(this.accountForm.value ?? {}).some(value => `${value ?? ''}`.trim() !== '');
    }

    get selectedAccount(): DebitoInmediatoSavedAccount | null {
        return this.accounts.find(account => account.id === this.selectedAccountId) ?? null;
    }

    get amountUSD(): number {
        return this.quote?.amounts?.amountUSD ?? 0;
    }

    get amountVES(): number {
        return this.quote?.amounts?.amountVES ?? 0;
    }

    get exchangeRateBCV(): number {
        return this.quote?.amounts?.exchangeRateBCV ?? 0;
    }

    /** El cliente ya reportó un pago de esta factura: conviene avisarle. */
    get billAlreadyReported(): boolean {
        return (this.quote?.billStatus ?? '').toLowerCase() === 'procesando';
    }

    get canRequestToken(): boolean {
        if (this.requestingToken || this.loadingQuote || !(this.amountVES > 0)) {
            return false;
        }
        // Sin bancos no se puede armar una cuenta nueva, pero una guardada sigue
        // sirviendo: sus datos los resuelve el servidor.
        return this.usingSavedAccount || (this.accountForm.valid && !this.banksUnavailable);
    }

    get canCharge(): boolean {
        return !this.charging && !this.otpExpired && this.otpForm.valid;
    }

    /** Cuenta regresiva de la clave, `m:ss`. */
    get otpCountdown(): string {
        const minutes = Math.floor(this.otpSecondsLeft / 60);
        const seconds = this.otpSecondsLeft % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    /** Solo tras un rechazo del banco: nunca tras un indeterminado. */
    get canRetryCharge(): boolean {
        return this.outcome?.status === 'rejected';
    }

    /**
     * El banco debitó y el pago no quedó registrado. Es raro y no se arregla
     * desde acá: lo resuelve soporte con la referencia. Lo único que importa en
     * pantalla es que el cliente NO pague de nuevo.
     */
    get chargedNotRegistered(): boolean {
        return this.outcome?.charged === true && this.outcome?.status !== 'charged';
    }

    /** El débito pudo haber salido de la cuenta: nada puede borrar este estado. */
    get hasUnresolvedCharge(): boolean {
        return this.outcome?.status === 'indeterminate' || this.outcome?.status === 'processing' || this.step === 'settling' || this.chargedNotRegistered;
    }

    /** Cuenta regresiva de la espera del banco, `m:ss`. */
    get settlementCountdown(): string {
        const minutes = Math.floor(this.settlementSecondsLeft / 60);
        const seconds = this.settlementSecondsLeft % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    /** El pago quedó registrado y en validación: no hay nada que reintentar. */
    get paymentInReview(): boolean {
        return this.outcome?.status === 'processing';
    }

    get outcomeBankDetail(): string | null {
        return formatBankDetail(this.outcome?.bankCode, this.outcome?.bankDetail);
    }

    /** Mensaje del teléfono: recién cuando el cliente sale del campo. */
    get phoneError(): string | null {
        const control = this.accountForm.get('phone');
        if (!control || control.valid || !control.touched) return null;

        const reason = control.errors?.['venezuelanMobile'];
        if (reason === 'prefix') {
            return `Debe ser un móvil (${VE_MOBILE_PREFIXES_TEXT}).`;
        }
        if (reason === 'length') {
            return 'Faltan o sobran dígitos. Ejemplo: 04141234567.';
        }
        return 'Escribe el teléfono de tu pago móvil.';
    }

    get cedulaError(): string | null {
        const control = this.accountForm.get('cedula');
        if (!control || control.valid || !control.touched) return null;

        const reason = control.errors?.['nationalId'];
        if (reason === 'prefix') {
            return 'Usa una sola letra al inicio (V, E, J, G, P o C).';
        }
        if (reason === 'digits') {
            return 'Revisa los dígitos. Ejemplo: V12345678.';
        }
        return 'Escribe la cédula del titular de la cuenta.';
    }

    bankLabel(code: string | null): string {
        if (!code) {
            return 'Banco no registrado';
        }
        return this.banks.find(bank => bank.code === code)?.name ?? `Banco ${code}`;
    }

    accountBank(account: DebitoInmediatoSavedAccount): string {
        return account.bankName || this.bankLabel(account.bankCode);
    }

    /** Cuenta con la que se está pagando, para repetirla en el paso de la clave. */
    get payingAccountLabel(): string {
        if (this.usingSavedAccount && this.selectedAccount) {
            return `${this.selectedAccount.phone} · ${this.accountBank(this.selectedAccount)}`;
        }

        const account = this.accountForm.getRawValue();
        const phone = String(account.phone ?? '').trim();
        return phone ? `${phone} · ${this.bankLabel(String(account.bankCode ?? '').trim())}` : '';
    }

    selectAccount(accountId: string): void {
        this.selectedAccountId = accountId;
        this.accountChosenByUser = true;
    }

    requestToken(): void {
        if (!this.canRequestToken) {
            if (!this.usingSavedAccount) {
                this.accountForm.markAllAsTouched();
            }
            return;
        }

        this.requestingToken = true;
        this.tokenErrorDetail = null;
        // El reloj arranca ACÁ, con la petición saliendo: el banco cuenta los 2
        // minutos desde que se le pide la clave, así que lo que tarde su
        // respuesta y el SMS ya es tiempo gastado de esa ventana.
        this.startOtpCountdown();
        this.plansService
            .requestDebitToken$(String(this.billPlanId), this.buildAccountInput(), this.changeOptions())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.requestingToken = false;
                    this.tokenSentTo = res?.data?.phone || '';
                    this.otpForm.reset({ otp: '' });
                    this.step = 'otp';
                },
                error: (err: HttpErrorResponse) => {
                    this.requestingToken = false;
                    // Sin clave enviada no hay nada que vencer.
                    this.stopOtpCountdown();
                    if (this.interceptCheckoutError(err, 'token')) {
                        return;
                    }
                    const body: any = err?.error || {};

                    // El detalle del banco queda a la vista (el toast se va): es el
                    // dato con el que soporte puede reclamarle el fallo al banco.
                    this.tokenErrorDetail = formatBankDetail(body?.data?.bankCode, body?.data?.bankDetail);
                    this.toastService.error(body.title || 'No se pudo enviar la clave', body.message || 'El banco no pudo enviarte la clave. Intenta de nuevo en unos minutos.');
                },
            });
    }

    charge(): void {
        if (!this.canCharge) {
            this.otpForm.markAllAsTouched();
            return;
        }

        this.charging = true;
        this.chargeInFlight.emit(true);
        // La clave ya se está usando: que no venza en pantalla mientras el banco
        // responde, ni siga corriendo sobre el resultado.
        this.stopOtpCountdown();
        this.plansService
            .chargeDebit$(String(this.billPlanId), this.buildAccountInput(), { otp: String(this.otpForm.get('otp')?.value ?? '').trim() }, this.changeOptions())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    // El banco aceptó pero todavía no movió el dinero (pasa
                    // cuando la cuenta es de otro banco). El pago YA quedó
                    // registrado: acá solo se espera la confirmación.
                    if (res?.data?.settlementPending === true) {
                        this.startSettlementWatch(res.data);
                        return;
                    }
                    this.settleOutcome(this.toSuccessOutcome(res));
                },
                error: (err: HttpErrorResponse) => {
                    // Los errores del checkout (cupo fiscal, venta de cupos apagada) ocurren
                    // antes del banco: no hubo débito, así que nunca son «Pago rechazado».
                    if (this.interceptCheckoutError(err, 'charge')) {
                        return;
                    }
                    this.settleOutcome(this.toErrorOutcome(err));
                },
            });
    }

    /**
     * 409 `EXTRA_USERS_SALES_DISABLED` en el cobro (ADM-790): el servidor lo corta antes de
     * debitar, así que no es un desenlace del pago. Nunca pasa por `toErrorOutcome` (lo
     * pintaría «Pago rechazado»): se libera el cobro y se vuelve a pedir la clave.
     */
    private releaseChargeForSalesDisabled(err: HttpErrorResponse): void {
        this.charging = false;
        this.chargeInFlight.emit(false);
        this.otpForm.reset({ otp: '' });
        this.step = 'account';
        this.notifyExtraUsersSalesDisabled(err);
    }

    /**
     * Venta de cupos apagada (ADM-790), en `quote`, `request-token` o `charge`: aviso con el
     * copy del servidor (no un rechazo) y bundle refrescado para recalcular los montos.
     */
    private notifyExtraUsersSalesDisabled(err: HttpErrorResponse): void {
        this.toastService.alert(err?.error?.title || EXTRA_USERS_SALES_DISABLED_TITLE, err?.error?.message || EXTRA_USERS_SALES_DISABLED_MESSAGE, 8000);
        this.plansService.obtainPlansBundle();
    }

    /**
     * Espera a que el banco del cliente confirme el débito, preguntando cada 3
     * segundos hasta que se defina o se agote su ventana.
     *
     * Esto NO es parte del pago: el pago ya está registrado (en validación). Si
     * el cliente cierra la ventana o se le va el internet, no pierde nada; solo
     * se queda sin ver el desenlace en vivo.
     */
    private startSettlementWatch(data: any): void {
        this.charging = false;
        this.settlementReference = data?.reference ?? null;
        this.step = 'settling';

        const deadline = Date.parse(String(data?.deadlineAt ?? ''));
        this.settlementDeadline = Number.isNaN(deadline) ? Date.now() + DebitoInmediatoPaymentComponent.SETTLEMENT_WINDOW_SECONDS * 1000 : deadline;
        this.refreshSettlementCountdown();

        this.analyticsService.analyticsCapture('Débito inmediato esperando liquidación', {
            reference: this.settlementReference,
        });

        this.settlementTicker = interval(DebitoInmediatoPaymentComponent.SETTLEMENT_POLL_MS)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.checkSettlement());
    }

    private checkSettlement(): void {
        this.refreshSettlementCountdown();
        if (this.settlementInFlight || !this.settlementReference) {
            return;
        }

        this.settlementInFlight = true;
        this.plansService
            .getDebitSettlement$(this.settlementReference)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.settlementInFlight = false;
                    this.resolveSettlement(res?.data, res?.message);
                },
                error: () => {
                    // No haber podido preguntar no dice nada del débito: se
                    // insiste hasta que la ventana se agote. El pago está
                    // registrado, así que fallar acá no lo pone en riesgo.
                    this.settlementInFlight = false;
                    if (this.settlementSecondsLeft <= 0) {
                        this.finishSettlementAsProcessing();
                    }
                },
            });
    }

    private resolveSettlement(data: any, message?: string): void {
        const settlement = String(data?.settlement ?? '');

        if (settlement === 'settled') {
            this.stopSettlementWatch();
            this.settleOutcome(
                {
                    status: 'charged',
                    charged: true,
                    reference: this.settlementReference,
                    requiresNewToken: false,
                    title: 'Pago confirmado',
                    message: message || 'Tu banco confirmó la operación y tu pago quedó registrado.',
                    bankCode: null,
                    bankDetail: null,
                },
                'Débito inmediato liquidado',
            );
            return;
        }

        if (settlement === 'rejected') {
            // El banco no completó la operación: no se debitó. El pago ya está
            // registrado, así que no se ofrece pagar de nuevo (serían dos pagos
            // reportados): queda en validación y lo resuelve FINA.
            this.stopSettlementWatch();
            this.settleOutcome(
                {
                    status: 'processing',
                    charged: false,
                    reference: this.settlementReference,
                    requiresNewToken: false,
                    title: 'Tu banco no completó la operación',
                    message: message || 'Tu banco no aprobó el débito. Tu pago quedó registrado y lo estamos validando: te escribimos en cuanto se resuelva.',
                    bankCode: data?.bankCode ?? null,
                    bankDetail: data?.bankDetail ?? null,
                },
                'Débito inmediato rechazado por el banco receptor',
            );
            return;
        }

        if (data?.expired === true || this.settlementSecondsLeft <= 0) {
            this.finishSettlementAsProcessing();
        }
    }

    /**
     * Se agotó la ventana del banco sin confirmación. El pago sigue registrado y
     * en validación: no se le pide al cliente que haga nada más.
     */
    private finishSettlementAsProcessing(): void {
        this.stopSettlementWatch();
        this.settleOutcome(
            {
                status: 'processing',
                charged: false,
                reference: this.settlementReference,
                requiresNewToken: false,
                title: 'Tu pago está en validación',
                message: 'Tu banco no confirmó la operación a tiempo. Tu pago quedó registrado y lo estamos validando: te escribimos en cuanto se resuelva.',
                bankCode: null,
                bankDetail: null,
            },
            'Débito inmediato sin confirmar del banco',
        );
    }

    private refreshSettlementCountdown(): void {
        if (this.settlementDeadline === null) {
            this.settlementSecondsLeft = 0;
            return;
        }
        this.settlementSecondsLeft = Math.max(0, Math.ceil((this.settlementDeadline - Date.now()) / 1000));
    }

    private stopSettlementWatch(): void {
        this.settlementTicker?.unsubscribe();
        this.settlementTicker = null;
        this.settlementDeadline = null;
        this.settlementSecondsLeft = 0;
        this.settlementInFlight = false;
    }

    /**
     * Vuelve al primer paso para pedir una clave nueva: la anterior ya no sirve
     * (la quemó un rechazo, o venció sin usarse). Se recotiza a propósito, porque
     * la clave nueva se ata al monto de ese momento.
     */
    retryWithNewToken(): void {
        this.outcome = null;
        this.otpForm.reset({ otp: '' });
        this.stopOtpCountdown();
        this.step = 'account';
        this.loadQuote();
    }

    /** Reintenta con la misma clave (rechazos que no son de la clave). */
    retryWithSameToken(): void {
        this.outcome = null;
        this.step = 'otp';
    }

    goBackToAccountStep(): void {
        if (this.charging) {
            return;
        }
        // La clave que quede viva en el banco ya no se va a usar desde acá: al
        // volver a "Continuar" se pide una nueva y el reloj arranca de cero.
        this.stopOtpCountdown();
        this.step = 'account';
    }

    /**
     * Arranca la cuenta regresiva de la clave.
     *
     * Los segundos se derivan de un instante guardado y no se van descontando de
     * uno en uno: si el navegador frena el tick (pestaña en segundo plano, móvil
     * bloqueado), al volver se ve el tiempo REAL que queda —o el vencimiento— en
     * vez de un reloj atrasado que dejaría cobrar con una clave que el banco ya
     * dio por muerta.
     */
    private startOtpCountdown(): void {
        this.stopOtpCountdown();
        this.otpRequestedAt = Date.now();
        this.otpSecondsLeft = DebitoInmediatoPaymentComponent.OTP_TTL_SECONDS;
        this.otpTicker = interval(1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshOtpCountdown());
    }

    private refreshOtpCountdown(): void {
        if (this.otpRequestedAt === null) {
            return;
        }

        const elapsedSeconds = (Date.now() - this.otpRequestedAt) / 1000;
        this.otpSecondsLeft = Math.max(0, Math.ceil(DebitoInmediatoPaymentComponent.OTP_TTL_SECONDS - elapsedSeconds));
        if (this.otpSecondsLeft > 0) {
            return;
        }

        // Venció sin usarse: se borra lo que el cliente hubiera escrito para que
        // no mande al banco una clave muerta, y se le pide una nueva.
        this.stopOtpCountdown();
        this.otpExpired = true;
        this.otpForm.reset({ otp: '' });
        this.analyticsService.analyticsCapture('Débito inmediato clave vencida');
    }

    private stopOtpCountdown(): void {
        this.otpTicker?.unsubscribe();
        this.otpTicker = null;
        this.otpRequestedAt = null;
        this.otpSecondsLeft = 0;
        this.otpExpired = false;
    }

    /**
     * Cierra el flujo con su desenlace y lo deja registrado en PostHog.
     *
     * `event` se pasa a mano cuando el desenlace viene de la espera de la
     * liquidación: ahí un mismo `processing` puede ser "el banco rechazó tarde" o
     * "se agotó la ventana", y en producción no da igual cuál de los dos fue.
     */
    private settleOutcome(outcome: DebitoInmediatoOutcome, event: string = OUTCOME_EVENTS[outcome.status]): void {
        this.charging = false;
        // Hay desenlace: se libera al contenedor ANTES de `chargeUnresolved`,
        // que para los desenlaces sin cerrar vuelve a anclar el paso 2 por su
        // propia vía (el candado del "no resuelto" reemplaza al del "en vuelo").
        this.chargeInFlight.emit(false);
        this.outcome = outcome;
        this.step = 'result';

        // La referencia del banco es la única llave que cruza este evento con los
        // logs del servidor y con la operación en BNC.
        this.analyticsService.analyticsCapture(event, {
            reference: outcome.reference,
            bankCode: outcome.bankCode,
            debited: outcome.charged === true,
        });

        if (outcome.status === 'charged') {
            this.paid.emit();
            return;
        }
        // `processing` avisa igual que un indeterminado: hay un pago registrado,
        // así que el contenedor no debe mostrar éxito ni ofrecer otra vía de pago
        // (serían dos pagos reportados por la misma factura).
        if (outcome.status === 'indeterminate' || outcome.status === 'processing') {
            this.chargeUnresolved.emit(outcome);
        }
    }

    private toSuccessOutcome(res: any): DebitoInmediatoOutcome {
        return {
            status: 'charged',
            charged: true,
            reference: res?.data?.ref ?? res?.data?.reference ?? null,
            requiresNewToken: false,
            title: 'Pago registrado',
            message: res?.message || 'Tu pago fue registrado correctamente.',
            bankCode: null,
            bankDetail: null,
        };
    }

    /**
     * 502/504 no son rechazos: el débito PUDO haberse ejecutado (o se ejecutó y el
     * registro falló, que el servidor marca con `data.charged`). Cualquier otro
     * error sí es un rechazo, con o sin banco de por medio (400 del banco, 409 de
     * la factura, 429 por exceso de intentos).
     */
    private toErrorOutcome(err: HttpErrorResponse): DebitoInmediatoOutcome {
        const body: any = err?.error || {};
        const data: any = body?.data || {};
        const isUnresolved = err?.status === 502 || err?.status === 504 || data?.indeterminate === true;

        const charged = data?.charged === true;
        return {
            status: isUnresolved ? 'indeterminate' : 'rejected',
            charged,
            reference: data?.reference ?? null,
            requiresNewToken: data?.requiresNewToken === true,
            // El título lo pone la app: los del backend están escritos para el
            // panel de Cobranzas ("Cobro rechazado"), que le habla a un agente. El
            // `message` sí sale del servidor, que es el que trae el motivo del banco.
            title: this.outcomeTitle(isUnresolved, charged),
            message: body?.message || (isUnresolved ? 'No pudimos confirmar el pago. No vuelvas a pagar: escríbenos para revisarlo.' : 'El banco no pudo procesar el pago.'),
            bankCode: data?.bankCode ?? null,
            bankDetail: data?.bankDetail ?? null,
        };
    }

    private outcomeTitle(isUnresolved: boolean, charged: boolean): string {
        if (charged) {
            // El dinero salió: para el cliente el pago está hecho y lo que falta
            // lo acredita soporte. Un título de error acá lo empujaría a pagar de
            // nuevo, que es lo único que no puede pasar.
            return 'Tu pago ya se debitó';
        }
        return isUnresolved ? 'Pago sin confirmar' : 'Pago rechazado';
    }

    /**
     * Único punto de intercepción de los errores del checkout que no son del banco
     * (ADM-740, ADM-790), en `quote`, `request-token` y `charge`. Corre DESPUÉS de la
     * limpieza propia de cada paso (en `quote`, tras descartar la respuesta obsoleta).
     * No solo atiende 409: también el 400 `FISCAL_QUOTA_REJECTED`.
     *
     * Devuelve `true` si atendió el error, y el manejador del paso termina ahí:
     * - 409 `EXTRA_USERS_SALES_DISABLED` (ADM-790): aviso con el copy del servidor y
     *   bundle refrescado. No emite `fiscalQuotaConflict`.
     * - errores del cupo fiscal: copy del servidor y `fiscalQuotaConflict`.
     */
    private interceptCheckoutError(err: HttpErrorResponse, step: 'quote' | 'token' | 'charge'): boolean {
        // Venta de cupos apagada (ADM-790). Su `errorCode` no choca con los fiscales.
        if (isExtraUsersSalesDisabledError(err)) {
            if (step === 'charge') {
                this.releaseChargeForSalesDisabled(err);
                return true;
            }
            if (step === 'quote') {
                this.quoteError = err?.error?.message || QUOTE_ERROR_FALLBACK;
            }
            this.notifyExtraUsersSalesDisabled(err);
            return true;
        }
        // Punto de extensión: la rama del 409 «Cupón reservado expirado» (handoff
        // ADM-646 `frontEnd-di-cupon-hold-expirado`) va aquí, junto a la fiscal: se
        // detecta por `title` y no lleva `errorCode`, así que las dos no se pisan.
        const issue = fiscalQuotaIssue(err, { intentSent: this.extraControlNumbers != null });
        if (!issue) {
            return false;
        }
        const body: any = err?.error || {};
        const title = body.title || 'Cambio de facturas no disponible';
        const message = body.message || 'No pudimos usar esa cantidad de facturas. Revisa tu selección e intenta de nuevo.';
        if (step === 'charge') {
            // Orden fijo: se libera el candado del contenedor antes de que vuelva al paso 1.
            this.charging = false;
            this.chargeInFlight.emit(false);
            this.step = 'account';
            this.otpForm.reset({ otp: '' });
        }
        if (step === 'quote') {
            this.quoteError = message;
        }
        if (issue === FISCAL_QUOTA_REJECTED) {
            this.toastService.error(title, message);
        } else {
            this.toastService.alert(title, message, 8000);
        }
        this.fiscalQuotaConflict.emit(issue);
        return true;
    }

    /** Lo mismo en `quote`, `request-token` y `charge`: si difiere, el banco rechaza la clave. */
    private changeOptions(): DebitoInmediatoChangeOptions {
        return {
            planType: this.planType,
            paymentFrequency: this.paymentFrequency,
            pendingPurchasedUsers: this.pendingPurchasedUsers,
            pendingBlockUserIds: this.pendingBlockUserIds,
            // Sin intención la clave no se añade: el body (y las aserciones exactas) siguen igual.
            ...(this.extraControlNumbers != null ? { extraControlNumbers: this.extraControlNumbers } : {}),
        };
    }

    private buildAccountInput(): DebitoInmediatoAccountInput {
        if (this.usingSavedAccount) {
            return { savedInstrumentId: this.selectedAccountId };
        }

        const account = this.accountForm.getRawValue();
        const bankCode = String(account.bankCode ?? '').trim();
        return {
            phone: String(account.phone ?? '').trim(),
            cedula: String(account.cedula ?? '').trim(),
            bankCode,
            bankName: this.bankLabel(bankCode),
            holderName: String(account.holderName ?? '').trim(),
        };
    }

    private loadQuote(): void {
        if (!this.eligible || !this.billPlanId) {
            return;
        }

        this.loadingQuote = true;
        this.quoteError = '';
        // El monto que queda en pantalla es el que el banco va a atar a la clave,
        // así que una cotización vieja que llegue tarde no puede pisar a la nueva:
        // se autoriza un débito por lo que se muestra, no por lo que respondió
        // primero.
        const request = ++this.quoteRequest;
        this.plansService
            .getDebitQuote$(this.billPlanId, this.changeOptions())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    if (request !== this.quoteRequest) {
                        return;
                    }
                    this.loadingQuote = false;
                    this.quote = res?.data ?? null;
                },
                error: (err: HttpErrorResponse) => {
                    if (request !== this.quoteRequest) {
                        return;
                    }
                    this.loadingQuote = false;
                    this.quote = null;
                    if (this.interceptCheckoutError(err, 'quote')) {
                        return;
                    }
                    // Sin monto no se puede pedir la clave: se muestra el motivo del
                    // servidor (factura no cobrable, cambio no permitido, sin tasa).
                    this.quoteError = err?.error?.message || QUOTE_ERROR_FALLBACK;
                },
            });
    }

    private loadAccounts(): void {
        this.plansService
            .getDebitInstruments$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.loadingAccounts = false;
                    this.accounts = Array.isArray(res?.data) ? res.data : [];
                    // Mientras estas cuentas viajaban, el cliente pudo haber
                    // empezado a cargar una nueva (el formulario se ve desde el
                    // principio). Preseleccionar acá le mandaría la clave al
                    // teléfono de otra cuenta sin que él lo pidiera.
                    if (this.accounts.length && !this.accountChosenByUser && !this.newAccountFormHasData) {
                        this.selectedAccountId = this.accounts[0].id;
                    }
                },
                error: () => {
                    // Sin cuentas guardadas el flujo sigue: se cargan datos nuevos.
                    this.loadingAccounts = false;
                    this.accounts = [];
                },
            });
    }

    private loadBanks(): void {
        this.plansService
            .getDebitBanks$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.banks = Array.isArray(res?.data?.banks) ? res.data.banks : [];
                    this.banksUnavailable = this.banks.length === 0;
                },
                error: () => {
                    this.banks = [];
                    this.banksUnavailable = true;
                },
            });
    }
}
